import React, { useState } from 'react';
import logo from '../../images/logo.png';
import axios from 'axios';
import './SeguimientoEncomienda.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReCAPTCHA from "react-google-recaptcha";
import { encryptData } from '../../utils/encryption';
import { validarRut, formatearRut, limpiarRut } from '../../utils/rutValidator';

function SeguimientoEncomienda() {
  const recaptchaToken = process.env.REACT_APP_PUBLIC_REAPCTCHA_TOKEN;
  const [captchaToken, setCaptchaToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const url = 'https://api-carga.tabsa.cl/api/encomienda/estado';
  
  // Estados para RUT
  const [rut, setRut] = useState('');
  const [rutFormateado, setRutFormateado] = useState('');
  const [rutValido, setRutValido] = useState(null);
  const [rutError, setRutError] = useState('');
  
  // Estado para encomienda
  const encomienda = useFormInput('');

  const handleCaptcha = (token) => {
    setCaptchaToken(token);
  };

  // Manejar cambio en input de RUT
  const handleRutChange = (e) => {
    const input = e.target.value;
    
    // Limpiar y limitar a números, K y caracteres de formato
    const cleaned = input.replace(/[^0-9kK.-]/g, '').toUpperCase();
    
    setRut(cleaned);
    setRutFormateado(cleaned);
    
    // Limpiar validación mientras escribe
    if (rutError) setRutError('');
    if (rutValido !== null) setRutValido(null);
  };

  // Validar RUT cuando pierde el foco
  const handleRutBlur = () => {
    if (!rut) {
      setRutValido(null);
      setRutError('');
      return;
    }
    
    const esValido = validarRut(rut);
    setRutValido(esValido);
    
    if (esValido) {
      // Formatear automáticamente si es válido
      const formatted = formatearRut(rut);
      setRutFormateado(formatted);
      setRutError('');
    } else {
      setRutError('RUT inválido. Verifica el dígito verificador.');
    }
  };

  const handleBuscar = async () => {
    // Validar RUT
    if (!rut || rut.trim() === '') {
      toast.error('Por favor ingrese un RUT', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      return;
    }

    const esRutValido = validarRut(rut);
    if (!esRutValido) {
      setRutValido(false);
      setRutError('RUT inválido. Verifica el dígito verificador.');
      toast.error('El RUT ingresado no es válido', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      return;
    }

    // Validar encomienda
    if (!encomienda.value || encomienda.value.trim() === '') {
      toast.error('Por favor ingrese el número de encomienda', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      return;
    }

    // Validar que la encomienda sea numérica
    if (!/^\d+$/.test(encomienda.value)) {
      toast.error('El número de encomienda debe contener solo números', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      return;
    }

    // Validar reCAPTCHA
    if (!captchaToken) {
      toast.error('Por favor complete el captcha', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      return;
    }

    setLoading(true);
    
    try {
      // Preparar RUT: sin puntos pero CON guión (ej: 19140775-K)
      const rutLimpio = limpiarRut(rut); // Quita puntos y guión
      const cuerpo = rutLimpio.slice(0, -1);
      const dv = rutLimpio.slice(-1);
      const rutParaEnviar = `${cuerpo}-${dv}`; // Formato: 19140775-K
      
      console.log('RUT a enviar:', rutParaEnviar);
      
      // Encriptar datos
      const encryptedEncomienda = encryptData(encomienda.value);
      const encryptedRut = encryptData(rutParaEnviar); // Envía con guión
      
      const data = {
        encomienda: encryptedEncomienda,
        rut: encryptedRut,
        recaptcha: captchaToken,
      };

      console.log('Enviando datos encriptados al servidor...');
      
      const response = await axios.post(url, data);
      
      if (response.data.data && response.data.data.length > 0) {
        setResultados(response.data.data);
        setMostrarResultados(true);
      } else {
        setResultados([]);
        setMostrarResultados(false);
        toast.error('No hay resultados para esta búsqueda.', {
          position: toast.POSITION.BOTTOM_CENTER
        });
      }
    } catch (error) {
      console.error('Error en la búsqueda:', error);
      toast.error('Error al realizar la búsqueda. Por favor intente nuevamente.', {
        position: toast.POSITION.BOTTOM_CENTER
      });
      setResultados([]);
      setMostrarResultados(false);
    } finally {
      setLoading(false);
    }
  };

  const handleNuevaBusqueda = () => {
    setMostrarResultados(false);
    setResultados([]);
    setRut('');
    setRutFormateado('');
    setRutValido(null);
    setRutError('');
    encomienda.reset();
    setCaptchaToken(null);
  };

  return (
    <div className="seguimiento-container">
      <ToastContainer />
      
      {/* Panel de búsqueda - Izquierda */}
      <div className="panel-busqueda">
        <div className="busqueda-content">
          {/* Logo */}
          <div className="logo-container">
            <img src={logo} alt="TABSA Logo" className="logo-imagen" />
          </div>
          
          <div className="descripcion">
            <label>Consulte el estado de su encomienda</label>
          </div>

          {/* Input de RUT con validación */}
          <div className="form-group">
            <label className="form-label">Documento de Identificación (RUT)</label>
            <input
              className={`form-input ${
                rutValido === true ? 'input-valido' : 
                rutValido === false ? 'input-invalido' : ''
              }`}
              type="text"
              placeholder="ej: 19876543-2"
              value={rutFormateado}
              onChange={handleRutChange}
              onBlur={handleRutBlur}
              disabled={loading}
              required
              maxLength={12}
            />
            {rutValido === true && (
              <span className="validation-message success">✓ RUT válido</span>
            )}
            {rutValido === false && (
              <span className="validation-message error">{rutError}</span>
            )}
          </div>

          {/* Input de encomienda */}
          <div className="form-group">
            <label className="form-label">Nº de Encomienda</label>
            <input
              className="form-input"
              type="text"
              placeholder="ej: 18987987998"
              {...encomienda}
              disabled={loading}
              required
              pattern="[0-9]*"
              inputMode="numeric"
            />
          </div>

          <div className="captcha-container">
            <ReCAPTCHA
              sitekey={recaptchaToken}
              onChange={handleCaptcha}
            />
          </div>

          <div className="form-group">
            <button
              type="button"
              className="btn-buscar"
              onClick={handleBuscar}
              disabled={loading}
            >
              {loading ? 'Buscando...' : 'Buscar'}
            </button>
            
            {mostrarResultados && (
              <button
                type="button"
                className="btn-nueva-busqueda"
                onClick={handleNuevaBusqueda}
              >
                Nueva Búsqueda
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Panel de resultados - Derecha */}
      <div className={`panel-resultados ${mostrarResultados ? 'visible' : ''}`}>
        {mostrarResultados ? (
          <div className="resultados-content">
            <h2 className="resultados-titulo">Seguimiento de Encomienda</h2>
            <div className="resultados-info">
              <p><strong>RUT:</strong> {rutFormateado}</p>
              <p><strong>Nº Encomienda:</strong> {encomienda.value}</p>
            </div>
            
            <div className="table-container">
              <table className="table-disponibilidad">
                <thead>
                  <tr>
                    <th className="th-black">Ítem</th>
                    <th className="th-black">Estado</th>
                    <th className="th-black">Última Actualización</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((resultado, index) => (
                    <tr key={index}>
                      <td className="td-black">{resultado.item}</td>
                      <td className="td-black">{resultado.descripcion}</td>
                      <td className="td-black">
                        {resultado.last_update.substring(0, 19)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="sin-resultados">
            <div className="sin-resultados-icon">📦</div>
            <p>Ingrese los datos de búsqueda para consultar el estado de su encomienda</p>
          </div>
        )}
      </div>
    </div>
  );
}

const useFormInput = (initialValue) => {
  const [value, setValue] = useState(initialValue);

  const handleChange = (e) => {
    setValue(e.target.value);
  };

  const reset = () => {
    setValue(initialValue);
  };

  return {
    value,
    onChange: handleChange,
    reset
  };
};

export default SeguimientoEncomienda;